import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit, X, DollarSign, Users } from 'lucide-react';
import { payStructureService } from '../api/mockRepo';
import { staffService } from '../../staff/api/mockRepo';
import type { PayStructure } from '../types';
import type { Staff } from '../../staff/types';
import toast from 'react-hot-toast';

const payStructureSchema = z.object({
  staff_id: z.string().min(1, 'Staff member is required'),
  basic_salary: z.number().min(0, 'Basic salary must be positive'),
  salary_type: z.enum(['monthly', 'hourly']),
  hourly_rate: z.number().min(0).optional(),
  house_rent: z.number().min(0).optional(),
  transport: z.number().min(0).optional(),
  medical: z.number().min(0).optional(),
  other_allowances: z.number().min(0).optional(),
  tax: z.number().min(0).optional(),
  provident_fund: z.number().min(0).optional(),
  insurance: z.number().min(0).optional(),
  loan: z.number().min(0).optional(),
  other_deductions: z.number().min(0).optional(),
  overtime_rate: z.number().min(0).optional(),
  effective_from: z.string().min(1, 'Effective date is required'),
  effective_to: z.string().optional()
});

type PayStructureFormData = z.infer<typeof payStructureSchema>;

export default function PayStructuresPage() {
  const [structures, setStructures] = useState<PayStructure[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStructure, setEditingStructure] = useState<PayStructure | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid }
  } = useForm<PayStructureFormData>({
    resolver: zodResolver(payStructureSchema),
    mode: 'onChange'
  });

  const salaryType = watch('salary_type');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [structuresData, staffData] = await Promise.all([
        payStructureService.getAll(),
        staffService.getAll()
      ]);
      setStructures(structuresData);
      setStaff(staffData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load pay structures');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: PayStructureFormData) => {
    console.log('onSubmit called with data:', data);
    console.log('Form errors:', errors);
    console.log('Form isValid:', isValid);
    try {
      if (editingStructure) {
        const updated = await payStructureService.update(editingStructure.id, data);
        setStructures(prev => prev.map(s => s.id === updated.id ? updated : s));
        toast.success('Pay structure updated successfully');
      } else {
        console.log('Creating new pay structure...');
        const created = await payStructureService.create(data);
        console.log('Created pay structure:', created);
        setStructures(prev => [created, ...prev]);
        toast.success('Pay structure created successfully');
      }
      
      setShowModal(false);
      setEditingStructure(null);
      reset();
    } catch (error: any) {
      console.error('Error in onSubmit:', error);
      toast.error(error.message || 'Operation failed');
    }
  };

  const onError = (errors: any) => {
    console.log('Form validation errors:', errors);
    toast.error('Please fix form validation errors');
  };

  const handleEdit = (structure: PayStructure) => {
    setEditingStructure(structure);
    reset({
      staff_id: structure.staff_id,
      basic_salary: structure.basic_salary,
      salary_type: structure.salary_type,
      hourly_rate: structure.hourly_rate,
      house_rent: structure.allowances.house_rent,
      transport: structure.allowances.transport,
      medical: structure.allowances.medical,
      other_allowances: structure.allowances.other,
      tax: structure.deductions.tax,
      provident_fund: structure.deductions.provident_fund,
      insurance: structure.deductions.insurance,
      loan: structure.deductions.loan,
      other_deductions: structure.deductions.other,
      overtime_rate: structure.overtime_rate,
      effective_from: structure.effective_from,
      effective_to: structure.effective_to
    });
    setShowModal(true);
  };

  const getStaffName = (staffId: string) => {
    const staffMember = staff.find(s => s.id === staffId);
    return staffMember ? staffMember.name : 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Pay Structures</h1>
          <p className="text-sm text-neutral-600">Manage salary structures for staff members</p>
        </div>
        <button
          onClick={() => {
            setEditingStructure(null);
            reset();
            setShowModal(true);
          }}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Pay Structure
        </button>
      </header>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <Users className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Active Structures</p>
              <p className="text-2xl font-bold text-neutral-900">
                {structures.filter(s => s.status === 'active').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <DollarSign className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Avg Basic Salary</p>
              <p className="text-2xl font-bold text-neutral-900">
                ${Math.round(structures.reduce((sum, s) => sum + s.basic_salary, 0) / structures.length || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <DollarSign className="w-8 h-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Monthly Structures</p>
              <p className="text-2xl font-bold text-neutral-900">
                {structures.filter(s => s.salary_type === 'monthly').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Pay Structures Table */}
      <div className="bg-white rounded-lg border">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Staff Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Basic Salary
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Allowances
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deductions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {structures.map((structure) => {
                const totalAllowances = (structure.allowances.house_rent || 0) + 
                                      (structure.allowances.transport || 0) + 
                                      (structure.allowances.medical || 0) + 
                                      (structure.allowances.other || 0);
                const totalDeductions = (structure.deductions.tax || 0) + 
                                      (structure.deductions.provident_fund || 0) + 
                                      (structure.deductions.insurance || 0) + 
                                      (structure.deductions.loan || 0) + 
                                      (structure.deductions.other || 0);
                
                return (
                  <tr key={structure.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {getStaffName(structure.staff_id)}
                      </div>
                      <div className="text-sm text-gray-500">
                        Effective: {structure.effective_from}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        ${structure.basic_salary.toLocaleString()}
                      </div>
                      {structure.hourly_rate && (
                        <div className="text-sm text-gray-500">
                          ${structure.hourly_rate}/hr
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        structure.salary_type === 'monthly' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {structure.salary_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${totalAllowances.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${totalDeductions.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        structure.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {structure.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(structure)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">
                {editingStructure ? 'Edit Pay Structure' : 'New Pay Structure'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit, onError)} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Staff Member *
                  </label>
                  <select
                    {...register('staff_id')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select staff member</option>
                    {staff.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.name} - {member.empCode}
                      </option>
                    ))}
                  </select>
                  {errors.staff_id && (
                    <p className="text-red-500 text-sm mt-1">{errors.staff_id.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Salary Type *
                  </label>
                  <select
                    {...register('salary_type')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="hourly">Hourly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Basic Salary *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('basic_salary', { valueAsNumber: true })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {errors.basic_salary && (
                    <p className="text-red-500 text-sm mt-1">{errors.basic_salary.message}</p>
                  )}
                </div>

                {salaryType === 'hourly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hourly Rate
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('hourly_rate', { valueAsNumber: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Effective From *
                  </label>
                  <input
                    type="date"
                    {...register('effective_from')}
                    defaultValue="2024-01-01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {errors.effective_from && (
                    <p className="text-red-500 text-sm mt-1">{errors.effective_from.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Effective To
                  </label>
                  <input
                    type="date"
                    {...register('effective_to')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Allowances Section */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-medium mb-3">Allowances</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      House Rent
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('house_rent', { valueAsNumber: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Transport
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('transport', { valueAsNumber: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Medical
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('medical', { valueAsNumber: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Other Allowances
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('other_allowances', { valueAsNumber: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Deductions Section */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-medium mb-3">Deductions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tax
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('tax', { valueAsNumber: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Provident Fund
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('provident_fund', { valueAsNumber: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Insurance
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('insurance', { valueAsNumber: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Loan
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('loan', { valueAsNumber: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Other Deductions
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('other_deductions', { valueAsNumber: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Overtime Rate
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('overtime_rate', { valueAsNumber: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Debug Info */}
              <div className="bg-gray-50 p-3 rounded text-xs">
                <div>Form Valid: {isValid ? 'Yes' : 'No'}</div>
                <div>Errors: {Object.keys(errors).length > 0 ? JSON.stringify(errors) : 'None'}</div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isValid}
                  className={`px-4 py-2 rounded-lg ${
                    isValid 
                      ? 'bg-blue-600 text-white hover:bg-blue-700' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {editingStructure ? 'Update' : 'Create'} Pay Structure
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
